import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

// Don't flash the banner for the initial connect or a quick reconnect.
const SHOW_DELAY_MS = 1500;
// How long "Connected" stays up after recovering.
const CONNECTED_HIDE_MS = 2500;

const useOnline = () => {
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
};

/**
 * Socket connection strip (chat, Trade Flash):
 * - offline            → "Waiting for connection..."
 * - online, no socket  → "Connecting to our server..."
 * - recovered          → "Connected" for a few seconds, then hides.
 */
const ConnectionStatusBanner = ({ isConnected, className = "" }) => {
  const { theme } = useSelector((state) => state.theme);
  const online = useOnline();

  // null | "offline" | "connecting" | "connected"
  const [status, setStatus] = useState(null);
  const shownRef = useRef(false);

  useEffect(() => {
    // A dropped network doesn't close a WebSocket right away, so trust the browser's
    // offline signal over the socket state.
    if (isConnected && online) {
      if (!shownRef.current) {
        setStatus(null);
        return;
      }
      setStatus("connected");
      const timer = setTimeout(() => {
        shownRef.current = false;
        setStatus(null);
      }, CONNECTED_HIDE_MS);
      return () => clearTimeout(timer);
    }

    const next = online ? "connecting" : "offline";
    if (shownRef.current) {
      setStatus(next);
      return;
    }
    const timer = setTimeout(() => {
      shownRef.current = true;
      setStatus(next);
    }, SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isConnected, online]);

  // Keep the last text while the strip collapses so it fades out instead of blanking.
  const lastStatus = useRef(null);
  if (status) lastStatus.current = status;
  const shown = lastStatus.current;

  const label =
    shown === "offline"
      ? "Waiting for connection..."
      : shown === "connecting"
      ? "Connecting to our server..."
      : shown === "connected"
      ? "Connected"
      : "";

  const dark = theme === "dark";
  const connected = shown === "connected";
  const bgClass = connected
    ? dark
      ? "bg-green-500/10"
      : "bg-green-50"
    : dark
    ? "bg-red-500/10"
    : "bg-red-50";
  const textClass = connected
    ? dark
      ? "text-green-400"
      : "text-green-600"
    : dark
    ? "text-red-400"
    : "text-red-600";

  return (
    <div
      role="status"
      aria-live="polite"
      className={`shrink-0 overflow-hidden transition-all duration-300 ${
        status ? "max-h-12 opacity-100" : "max-h-0 opacity-0"
      } ${bgClass} ${className}`}
    >
      <p className={`py-2 text-center text-sm font-semibold ${textClass}`}>
        {label}
      </p>
    </div>
  );
};

export default ConnectionStatusBanner;
